import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Tv,
  ListVideo,
  Clock,
  History,
  Globe,
  LogOut,
  Search,
  Plus,
  Trash2,
  Home
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParentDashboard = ({ onLogout, onBackHome }) => {
  const { t, language, setLanguage } = useApp();
  const [activeTab, setActiveTab] = useState('channels');
  const [channels, setChannels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [timeLimit, setTimeLimit] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newTimeLimit, setNewTimeLimit] = useState(120);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === 'channels') {
        const response = await axios.get(`${API}/channels`);
        setChannels(response.data);
      } else if (activeTab === 'playlists') {
        const response = await axios.get(`${API}/playlists`);
        setPlaylists(response.data);
      } else if (activeTab === 'history') {
        const response = await axios.get(`${API}/watch-history`);
        setWatchHistory(response.data);
      } else if (activeTab === 'timeLimit') {
        const response = await axios.get(`${API}/time-limits/status`);
        setTimeLimit(response.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const searchChannels = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/channels/search`,
        null,
        {
          params: {
            query: searchQuery
          }
        }
      );
      setSearchResults(response.data.channels);
    } catch (error) {
      console.error('Error searching channels:', error);
      alert('Error searching channels. Please try again.');
    }
    setLoading(false);
  };

  const addChannel = async (channel) => {
    try {
      await axios.post(`${API}/channels`, {
        channel_id: channel.channel_id,
        title: channel.title,
        thumbnail: channel.thumbnail,
        description: channel.description
      });
      setSearchResults([]);
      setSearchQuery('');
      loadData();
    } catch (error) {
      console.error('Error adding channel:', error);
    }
  };

  const deleteChannel = async (channelId) => {
    if (window.confirm('Are you sure you want to remove this channel?')) {
      try {
        await axios.delete(`${API}/channels/${channelId}`);
        loadData();
      } catch (error) {
        console.error('Error deleting channel:', error);
      }
    }
  };

  const updateTimeLimit = async () => {
    try {
      const passcode = prompt('Enter your passcode to update time limit:');
      if (!passcode) return;
      
      await axios.post(
        `${API}/settings/update-time-limit`,
        null,
        {
          params: {
            passcode: passcode,
            limit_minutes: newTimeLimit
          }
        }
      );
      loadData();
      alert('Time limit updated!');
    } catch (error) {
      console.error('Error updating time limit:', error);
      alert('Error updating time limit. Please check your passcode.');
    }
  };

  const tabs = [
    { id: 'channels', icon: Tv, label: t('channels') },
    { id: 'playlists', icon: ListVideo, label: t('playlists') },
    { id: 'history', icon: History, label: t('history') },
    { id: 'timeLimit', icon: Clock, label: t('timeLimit') },
    { id: 'settings', icon: Settings, label: t('settings') }
  ];

  return (
    <div className="flex h-screen bg-slate-50" data-testid="parent-dashboard">
      <div className="w-64 bg-white shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-8 text-gray-800 fredoka">Munasib</h1>

        <nav className="space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={20} />
                {tab.label}
              </button>
            );
          })}
        </nav>

        <button
          onClick={onBackHome}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-blue-600 hover:bg-blue-50 mt-4 transition-colors"
          data-testid="back-home-button"
        >
          <Home size={20} />
          {t('backToHome')}
        </button>

        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-red-600 hover:bg-red-50 mt-2 transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={20} />
          {t('logout')}
        </button>
      </div>

      <div className="flex-1 overflow-auto p-8">
        {activeTab === 'channels' && (
          <div data-testid="channels-tab">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 fredoka">{t('channels')}</h2>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">{t('searchChannels')}</h3>
              <div className="flex gap-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchChannels')}
                  className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  data-testid="search-input"
                  onKeyPress={(e) => e.key === 'Enter' && searchChannels()}
                />
                <button
                  onClick={searchChannels}
                  disabled={loading}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-8 py-3 rounded-lg transition-colors disabled:bg-gray-400"
                  data-testid="search-button"
                >
                  <Search size={20} />
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-auto">
                  {searchResults.map((channel) => (
                    <div
                      key={channel.channel_id}
                      className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50"
                      data-testid={`search-result-${channel.channel_id}`}
                    >
                      <img
                        src={channel.thumbnail}
                        alt={channel.title}
                        className="w-16 h-16 rounded-full"
                      />
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800">{channel.title}</h4>
                        <p className="text-sm text-gray-600 line-clamp-1">
                          {channel.description}
                        </p>
                      </div>
                      <button
                        onClick={() => addChannel(channel)}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
                        data-testid={`add-channel-${channel.channel_id}`}
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">{t('approvedChannels')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {channels.map((channel) => (
                  <div
                    key={channel.id}
                    className="border rounded-lg p-4 flex items-center gap-4"
                    data-testid={`approved-channel-${channel.channel_id}`}
                  >
                    <img
                      src={channel.thumbnail}
                      alt={channel.title}
                      className="w-16 h-16 rounded-full"
                    />
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800">{channel.title}</h4>
                    </div>
                    <button
                      onClick={() => deleteChannel(channel.channel_id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-channel-${channel.channel_id}`}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'playlists' && (
          <div data-testid="playlists-tab">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 fredoka">{t('playlists')}</h2>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-gray-600">Playlists feature coming soon! Parents will be able to create custom playlists from approved videos.</p>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div data-testid="history-tab">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 fredoka">{t('history')}</h2>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="space-y-4">
                {watchHistory.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <h4 className="font-bold text-gray-800">{item.video_title}</h4>
                      <p className="text-sm text-gray-600">{item.channel_title}</p>
                      <p className="text-xs text-gray-500">
                        Watched {Math.floor(item.duration_watched_seconds / 60)} minutes
                      </p>
                    </div>
                    <p className="text-sm text-gray-500">
                      {new Date(item.watched_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'timeLimit' && timeLimit && (
          <div data-testid="time-limit-tab">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 fredoka">{t('timeLimit')}</h2>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2 text-gray-800">{t('timeUsedToday')}</h3>
                <p className="text-4xl font-bold text-blue-500">
                  {timeLimit.used_today_minutes} / {timeLimit.daily_limit_minutes} min
                </p>
                <div className="w-full bg-gray-200 rounded-full h-4 mt-4">
                  <div
                    className="bg-blue-500 h-4 rounded-full transition-all"
                    style={{
                      width: `${Math.min(
                        (timeLimit.used_today_minutes / timeLimit.daily_limit_minutes) * 100,
                        100
                      )}%`
                    }}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{t('dailyLimit')}</h3>
                <div className="flex gap-4">
                  <input
                    type="number"
                    value={newTimeLimit}
                    onChange={(e) => setNewTimeLimit(parseInt(e.target.value))}
                    className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    data-testid="time-limit-input"
                  />
                  <button
                    onClick={updateTimeLimit}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-8 py-3 rounded-lg transition-colors"
                    data-testid="save-time-limit-button"
                  >
                    {t('save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div data-testid="settings-tab">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 fredoka">{t('settings')}</h2>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">
                <Globe className="inline mr-2" />
                Language
              </h3>
              <div className="flex gap-4">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-6 py-3 rounded-lg font-bold transition-colors ${
                    language === 'en'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  data-testid="language-en"
                >
                  English
                </button>
                <button
                  onClick={() => setLanguage('ar')}
                  className={`px-6 py-3 rounded-lg font-bold transition-colors ${
                    language === 'ar'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  data-testid="language-ar"
                >
                  عربي (Arabic)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;
